import React from "react";
import { generatedAssumptionImpactLedger } from "../data/generatedAssumptionImpactLedger.js";
import { generatedEconomicsFrontierReport } from "../data/generatedEconomicsFrontier.js";
import { generatedModelRunLedger } from "../data/generatedModelRunLedger.js";
import { generatedScenarioComparisonBoard } from "../data/generatedScenarioComparisonBoard.js";
import { generatedScenarioDecisionLedger } from "../data/generatedScenarioDecisionLedger.js";
import { generatedScenarioDecisionDeltaLedger } from "../data/generatedScenarioDecisionDeltaLedger.js";
import { generatedScenarioDiligenceWorkOrderLedger } from "../data/generatedScenarioDiligenceWorkOrderLedger.js";
import { generatedScenarioUncertaintyBudgetLedger } from "../data/generatedScenarioUncertaintyBudgetLedger.js";
import { scenarios } from "../data/snapshots.js";
import {
  round,
  scenarioBusinessCases,
  scenarioUncertaintyAnalysis,
  sensitivityAnalysis,
} from "../science/kernel.mjs";
import {
  AssumptionImpactGatesPanel,
  BusinessCaseFitPanel,
  DecisionDeltaAcceptanceGatesPanel,
  EconomicsFrontierPanel,
  ModelRunComparisonLedgerPanel,
  ModelRunReplayContractsPanel,
  RankingExtremesPanel,
  RuntimeEvidenceContractPanel,
  SelectedScenarioPanel,
  SensitivitySnapshotsPanel,
  UncertaintyBudgetPanel,
  UncertaintyRunPanel,
  WeightPanel,
} from "../components/ScenarioComparePanels.jsx";
import {
  DataTable,
  EvidencePanel,
  Metric,
  PageHeader,
  ScoreBarCell,
} from "../components/ui.jsx";

export function ScenarioCompare({
  rankedScenarios,
  selectedScenario,
  selectedScenarioId,
  setSelectedScenarioId,
  weights,
  setWeights,
  scenarioSet,
  setScenarioSet,
  horizonYears,
  setHorizonYears,
  capitalUsdBn,
  setCapitalUsdBn,
  launchMassT,
  setLaunchMassT,
  recordApproval,
  uncertaintyAnalysis,
  businessCases,
}) {
  const sensitivity = sensitivityAnalysis(scenarios, weights);
  const scenarioUncertainty =
    uncertaintyAnalysis ??
    scenarioUncertaintyAnalysis(scenarios, {
      weights,
      iterations: 256,
      seed: "scenario-compare-fallback",
    });
  const criteria = [
    ["energy", "Energy", (scenario) => scenario.benefits.energy],
    ["vacuum", "Vacuum", (scenario) => scenario.benefits.vacuum],
    ["optionality", "Strategic optionality", (scenario) => scenario.benefits.optionality],
    ["processFit", "Process fit", (scenario) => scenario.benefits.processFit],
    ["resourceConfidence", "Resource confidence", (scenario) => scenario.benefits.resourceConfidence],
    ["transport", "Transport", (scenario) => scenario.risks.transport],
    ["thermal", "Thermal", (scenario) => scenario.risks.thermal],
    ["comms", "Comms", (scenario) => scenario.risks.comms],
    ["feedstock", "Feedstock", (scenario) => scenario.risks.feedstock],
    ["processMaturity", "Process maturity", (scenario) => scenario.risks.processMaturity],
    ["utility", "Utility", (scenario) => scenario.scores.utility],
  ];
  const selectedUncertainty =
    scenarioUncertainty.summaries.find((summary) => summary.scenarioId === selectedScenario.id) ??
    scenarioUncertainty.summaries[0];
  const scenarioBusinessCasesForView =
    businessCases ??
    scenarioBusinessCases(scenarios, {
      weights,
      capitalUsdBn,
      launchMassT,
      horizonYears,
    });
  const selectedBusinessCase =
    scenarioBusinessCasesForView.find((entry) => entry.scenarioId === selectedScenario.id) ??
    scenarioBusinessCasesForView[0];
  const selectedDecision =
    generatedScenarioDecisionLedger.decisions.find((decision) => decision.scenarioId === selectedScenario.id) ??
    generatedScenarioDecisionLedger.decisions[0];
  const selectedEconomicsFrontier =
    generatedEconomicsFrontierReport.scenarioSummaries.find(
      (summary) => summary.scenarioId === selectedScenario.id
    ) ?? generatedEconomicsFrontierReport.scenarioSummaries[0];
  const selectedUncertaintyBudget =
    generatedScenarioUncertaintyBudgetLedger.budgets.find(
      (budget) => budget.scenarioId === selectedScenario.id
    ) ?? generatedScenarioUncertaintyBudgetLedger.budgets[0];
  const selectedAssumptionImpacts = generatedAssumptionImpactLedger.assumptionImpacts.filter((impact) =>
    impact.affectedScenarioIds.includes(selectedScenario.id)
  );
  const selectedModelRuns = generatedModelRunLedger.runs.filter(
    (run) => run.selectedScenario.scenarioId === selectedScenario.id
  );
  const replayRows = selectedModelRuns.length > 0 ? selectedModelRuns : generatedModelRunLedger.runs;
  const selectedDecisionDeltas = generatedScenarioDecisionDeltaLedger.deltaRecords.filter((record) =>
    record.affectedScenarioIds.includes(selectedScenario.id)
  );
  const decisionDeltaRows =
    selectedDecisionDeltas.length > 0
      ? selectedDecisionDeltas
      : generatedScenarioDecisionDeltaLedger.deltaRecords.slice(0, 4);
  const diligenceOrdersById = new Map(
    generatedScenarioDiligenceWorkOrderLedger.workOrders.map((order) => [order.id, order])
  );
  const selectedDiligenceOrders = decisionDeltaRows
    .map((record) => diligenceOrdersById.get(record.workOrderId))
    .filter(Boolean);

  function updateWeight(group, key, value) {
    setWeights((current) => ({
      ...current,
      [group]: {
        ...current[group],
        [key]: Number(value),
      },
    }));
  }

  return (
    <section className="module">
      <PageHeader
        title="SCENARIO COMPARE"
        muted="Industrial utility"
        subtitle="Live scenario recomputation with explainable score drivers and sensitivity controls."
      />
      <div className="compare-toolbar panel">
        <label>
          Scenario set
          <select value={scenarioSet} onChange={(event) => setScenarioSet(event.target.value)}>
            <option value="Primary set (6)">Primary set (6)</option>
            <option value="Conservative set">Conservative set</option>
            <option value="Risk-sensitive set">Risk-sensitive set</option>
          </select>
        </label>
        <label>
          Horizon years
          <input
            type="range"
            min="5"
            max="30"
            step="1"
            value={horizonYears}
            onChange={(event) => setHorizonYears(Number(event.target.value))}
          />
          <strong>{horizonYears}</strong>
        </label>
        <label>
          Capital available ($Bn)
          <input
            type="number"
            min="5"
            max="900"
            step="5"
            value={capitalUsdBn}
            onChange={(event) => setCapitalUsdBn(Number(event.target.value))}
          />
        </label>
        <label>
          Launch mass (t)
          <input
            type="number"
            min="20"
            max="900"
            step="10"
            value={launchMassT}
            onChange={(event) => setLaunchMassT(Number(event.target.value))}
          />
        </label>
      </div>

      <EvidencePanel title="Scenario comparison board" status={generatedScenarioComparisonBoard.mode} className="panel-large">
        <p>{generatedScenarioComparisonBoard.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Board hash" value={generatedScenarioComparisonBoard.boardHash.slice(0, 18)} />
          <Metric label="Selected" value={generatedScenarioComparisonBoard.selectedScenarioName} />
          <Metric label="CSV export" value={generatedScenarioComparisonBoard.exportManifest.csvHash.slice(0, 18)} />
          <Metric
            label="Markdown export"
            value={generatedScenarioComparisonBoard.exportManifest.markdownHash.slice(0, 18)}
          />
        </div>
        <DataTable
          compact
          rows={generatedScenarioComparisonBoard.rows}
          getRowKey={(row) => row.scenarioId}
          columns={[
            { key: "rank", header: "Rank" },
            { key: "name", header: "Scenario" },
            { key: "decisionLane", header: "Lane" },
            { key: "decisionReadinessScore", header: "Readiness" },
            { key: "deterministicUtility", header: "Utility" },
            { key: "uncertaintyBudgetPoints", header: "Uncertainty" },
            { key: "businessReadinessMedian", header: "Business readiness" },
            {
              key: "runtimeEvidence",
              header: "Runtime evidence",
              render: (row) => `${row.runtimeEvidenceCoveragePercent}% ${row.runtimeEvidenceState}`,
            },
            { key: "winProbabilityPercent", header: "Win %" },
            {
              key: "topBlocker",
              header: "Top blocker",
              render: (row) => row.topBlockers.split("; ")[0],
            },
            {
              key: "comparisonHash",
              header: "Hash",
              render: (row) => <code>{row.comparisonHash.slice(0, 18)}</code>,
            },
          ]}
        />
      </EvidencePanel>

      <div className="compare-grid">
        <EvidencePanel title="Weighted scoring matrix" status="Interactive" tone="green" className="panel-large">
          <DataTable
            rows={criteria.map(([key, label, resolver]) => ({ key, label, resolver }))}
            getRowKey={(row) => row.key}
            columns={[
              { key: "label", header: "Criterion" },
              ...rankedScenarios.map((scenario) => ({
                key: scenario.id,
                header: scenario.name,
                headerClassName: selectedScenarioId === scenario.id ? "col-selected" : "",
                cellClassName: () => (selectedScenarioId === scenario.id ? "col-selected" : ""),
                render: (row) => {
                  const value = row.key === "utility" ? scenario.scores.utility : row.resolver(scenario);
                  const isRisk =
                    row.key.startsWith("transport") ||
                    row.key === "thermal" ||
                    row.key === "comms" ||
                    row.key === "feedstock" ||
                    row.key === "processMaturity";

                  return <ScoreBarCell value={value} tone={isRisk ? "red" : "blue"} label={round(value, 0)} />;
                },
              })),
            ]}
          />
        </EvidencePanel>

        <SelectedScenarioPanel
          selectedScenario={selectedScenario}
          selectedUncertainty={selectedUncertainty}
          selectedBusinessCase={selectedBusinessCase}
          weights={weights}
          recordApproval={recordApproval}
        />
      </div>

      <RuntimeEvidenceContractPanel selectedDecision={selectedDecision} />

      <div className="compare-slider-grid">
        <WeightPanel
          title="Benefit weights"
          status="live"
          tone="green"
          weightGroup={weights.benefits}
          group="benefits"
          updateWeight={updateWeight}
        />
        <WeightPanel
          title="Risk weights"
          status="sensitivity"
          tone="red"
          weightGroup={weights.risks}
          group="risks"
          updateWeight={updateWeight}
        />
        <SensitivitySnapshotsPanel sensitivity={sensitivity} />
      </div>

      <BusinessCaseFitPanel scenarioBusinessCases={scenarioBusinessCasesForView} />
      <EconomicsFrontierPanel selectedEconomicsFrontier={selectedEconomicsFrontier} />
      <UncertaintyRunPanel scenarioUncertainty={scenarioUncertainty} />
      <UncertaintyBudgetPanel selectedUncertaintyBudget={selectedUncertaintyBudget} />
      <AssumptionImpactGatesPanel selectedAssumptionImpacts={selectedAssumptionImpacts} />

      <ModelRunReplayContractsPanel replayRows={replayRows} />
      <ModelRunComparisonLedgerPanel />
      <DecisionDeltaAcceptanceGatesPanel
        decisionDeltaRows={decisionDeltaRows}
        selectedDiligenceOrders={selectedDiligenceOrders}
      />
      <RankingExtremesPanel sensitivity={sensitivity} />
    </section>
  );
}
